import React from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import { List, OrderedMap, Map } from "immutable";
import { ParentSize } from "@vx/responsive";
import TableColumnSelector from "./table-column-selector";
import TableFilters from "./table-filters";
import TableFooter from "./table-footer";
import { EditableCell, EditableRow } from "./editable-table-components";
import _isEqual from "lodash/isEqual";
import _noop from "lodash/noop";
import "styles/table.css";
import {
    Table as AntTable,
    Row,
    Col,
    Card,
    Button,
    Badge,
    Pagination,
    Tag,
    Select,
    Drawer
} from "antd";
// import XLSX from "xlsx";
import { getTableExtraScrollPercent as getScrollPercent } from "utilities";
import moment from "moment";
// import _debounce from "lodash/debounce";

const DOCUMENT_FILTER_OPTIONS = new OrderedMap({
    "Have Documents": true,
    "Don't have Documents": false,
    "All": "all"
});

const TableFilterButton = ({toggleColumnSelector}) => (
    <Button onClick={toggleColumnSelector} type="default" icon={"switcher"}>
        Columns
    </Button>
);
const TableColumnSelectorDropdown = ({ columns, checkedColumns, onSelect, columnLimit, showColumnSelectorDrawer, toggleColumnSelector }) => (
    <React.Fragment>
        <Drawer
            placement="right"
            closable={false}
            width={350}
            title={"Column Selector"}
            onClose={toggleColumnSelector}
            visible={showColumnSelectorDrawer}
        >
            <TableColumnSelector {...{ columns, checkedColumns, onSelect, columnLimit }} />
        </Drawer>
        <TableFilterButton toggleColumnSelector={toggleColumnSelector} />
    </React.Fragment>
);

TableFilterButton.propTypes = {
    toggleColumnSelector: PropTypes.func
};

TableColumnSelectorDropdown.propTypes = {
    columns: PropTypes.object,
    checkedColumns: PropTypes.object,
    onSelect: PropTypes.func,
    columnLimit: PropTypes.oneOfType([PropTypes.func, PropTypes.number]),
    toggleColumnSelector: PropTypes.func,
    showColumnSelectorDrawer: PropTypes.bool
};

class Table extends React.Component {
    state = {
        selecttimestamp: null,
        selectedColumn: null,
        selectedDatatype: null,
        selectedData: [],
        showColumnSelectorDrawer: false
    };
    TableData = [];
    checkedColumns = this.getFilteredColumns(this.props.checkedColumns);
    filteredColumns = this.getFilteredColumns(this.props.filterColumns);
    parentWidth = 0;
    ownWidth = this.getTableWidth(this.checkedColumns);
    filters = this.filteredColumns.map(() => []).toJS(); // Base Object containing filters applied to table
    selectedRowKeys = new List([]); // Array of keys containing selected rows
    bulkActionSelectedRows = [];
    isEntireTableSelected = false;
    getTableWidth(checkedColumns) {
        return checkedColumns
            .toList()
            .reduce((agg, _) => (_.width ? agg + _.width : agg), 0);
    }
    updateParentWidth = (parent = {}) => {
        if (this.parentWidth === 0 && parent.width) {
            this.fixColumns(parent.width);
        }
        this.parentWidth = parent.width || 0;
    };
    //COLUMN FILTERING
    getFilteredColumns(checkedColumnsArray = [], columns) {
        if (checkedColumnsArray.length > 0) {
            return (columns || this.props.columns).filter(_ => 
                checkedColumnsArray.includes(_.key)
            );
        }
        return columns || this.props.columns;
    }

    filterColumns = (fList, selectAll) => {
        this.checkedColumns = this.getFilteredColumns(selectAll ? [] : fList);
        // Refresh Row Filters
        this.filters = this.checkedColumns
            .map((_, k) => this.filters[k] || [])
            .toJS();
        // Update Table Width
        this.ownWidth = this.getTableWidth(this.checkedColumns);
        this.fixColumns(this.parentWidth);
        // Update Selected Filters
        this.props.onColumnsUpdate(this.checkedColumns.map(_ => Map(_)));
        this.forceUpdate();
        
    };

    //COLUMN FIXER
    fixColumns = parentWidth => {
        const { fixedColsLeft, fixedColsRight } = this.props;
        if (this.ownWidth > parentWidth) {
            this.checkedColumns = this.checkedColumns.map(_ => {
                if (fixedColsLeft.includes(_.key)) {
                    return { ..._, fixed: "left" };
                }
                if (fixedColsRight.includes(_.key)) {
                    return { ..._, fixed: "right" };
                }
                return _;
            });
        }
    };
    //ROW SELECTION
    updateEntireTableSelection = (selected = false) =>
        (this.isEntireTableSelected = selected);
    updateSelectedRows = selectedRowKeys => {
        (this.selectedRowKeys = selectedRowKeys);
        this.props.onRowSelection(selectedRowKeys);
    }

    onRowSelect = (record, selected) => {
        if(selected) {
            this.bulkActionSelectedRows.push(record[this.props.bulkActionSelectedKey]);
        } else {
            this.bulkActionSelectedRows = this.bulkActionSelectedRows.filter(_ => _ !== record[this.props.bulkActionSelectedKey]);
        }
        this.updateSelectedRows(
            selected
                ? this.selectedRowKeys.push(record[this.props.keyFilter]) // push a new row on select
                : this.selectedRowKeys.filter(
                    _ => _ !== record[this.props.keyFilter]
                ) // remove a row on deselect
        );
        this.setState({
            selecttimestamp: new Date().toString()
        });
    };
    onAllRowsSelect = selected => {
        const { keyFilter } = this.props;
        this.updateEntireTableSelection(selected);
        if (selected) {
            this.bulkActionSelectedRows = this.filterRows()
                .map(_ => _[this.props.bulkActionSelectedKey])
                .toList();
            this.updateSelectedRows(
                this.filterRows()
                    .map(_ => _[keyFilter])
                    .toList()
            );
        } else {
            this.updateSelectedRows(new List([]));
        }
        this.setState({
            selecttimestamp: new Date().toString()
        });
    };

    getRowSelectionConfig = () => {
        if (this.props.noRowSelection) {
            return null;
        }
        return {
            type: "checkbox",
            selectedRowKeys: this.selectedRowKeys,
            onSelect: this.onRowSelect,
            onSelectAll: this.onAllRowsSelect,
            getCheckboxProps: record => ({
                disabled: !record[this.props.bulkActionSelectedKey],
                key: this.props.bulkActionSelectedKey
            }),
        };
    };
    // ROW FILTERING
    filterRows = selectedRows => {
        const { data } = this.props;
        return (selectedRows || data)
            .filter(row => this.applyFilterOnRow(row))
            .filter(_ => {
                if (
                    this.isDataFiltered ||
                    (!this.filters.search || this.filters.search.length === 0)
                ) {
                    return true;
                } else {
                    return new Map(_).reduce((agg, curr) => {
                        if (typeof curr === "string") {
                            return (
                                agg ||
                                curr
                                    .toLowerCase()
                                    .includes(
                                        this.filters.search[0].toLowerCase()
                                    )
                            );
                        }
                        return agg || false;
                    }, false);
                }
            });
    };

    applyFilterOnRow = row =>
        this.filteredColumns
            .map(({ onFilter }, columnName) =>
                this.filters[columnName] && this.filters[columnName][0] && onFilter
                    ? onFilter(this.filters[columnName][0], row)
                    : true
            )
            .reduce(
                (isFiltered, columnResult) => isFiltered && columnResult,
                true
            );

    // DATA DOWNLOAD
    onDownloadXlsx = async () => {
        return import(/* webpackChunkName: "xlsx" */ "xlsx")
            .then(_ => _.default)
            .then(async XLSX => {

                const { keyFilter } = this.props;
                const data = this.filterRows(
                    this.props.data
                        .filter(_ =>
                            this.selectedRowKeys.includes(_[keyFilter] || _.key)
                        )
                        .map(_ => {
                            return this.props.columns
                                .mapEntries(([k, v]) => [
                                    v.title,
                                    _[k] && _[k].format
                                        ? _[k] && _[k].format("DD-MM-YYYY")
                                        : _[k]
                                ])
                                .toJS();
                        })
                );
                let ws = await XLSX.utils.json_to_sheet(this.props.ignoreKeyFilter ? this.props.data.map(_ => {
                    delete _.id;
                    return _;
                }).toArray() : data.toArray(), {header: Object.keys(data.toArray()[0]).filter(_ =>_ !== "Action")});
                let wb = await XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, "SheetJS");
                const wbout = XLSX.writeFile(wb, `${new Date().toISOString()}_sales.xlsx`);
                return wbout;
            });
    };


    componentWillReceiveProps(nextProps) {
        const { data } = nextProps;
        this.selectedRowKeys = this.selectedRowKeys.reduce(
            (_, k) => (data.has(k) ? _.push(k) : _),
            new List([])
        );

        this.bulkActionSelectedRows = this.bulkActionSelectedRows.reduce(
            (_, k) => (data[this.props.bulkActionSelectedKey] === k ? [..._, k] : _),
            []
        );
        if (
            !_isEqual(nextProps.columns, this.props.columns) ||
            !_isEqual(nextProps.checkedColumns, this.props.checkedColumns) ||
            !_isEqual(nextProps.filterColumns.length, this.props.filterColumns.length)
        ) {
            this.filteredColumns = this.getFilteredColumns(
                nextProps.filterColumns,
                nextProps.columns
            );
            this.checkedColumns = this.getFilteredColumns(
                nextProps.checkedColumns,
                nextProps.columns
            );
            this.ownWidth = this.getTableWidth(this.checkedColumns);
            // this.filters = this.checkedColumns.map(() => []).toJS(); // Base Object containing filters applied to table
            this.fixColumns(this.parentWidth);
            this.selectedRowKeys = new List([]); // Array of keys containing selected rows
            this.isEntireTableSelected = false;
            this.forceUpdate();
        }
        if (nextProps.selectedRowKeys && nextProps.selectedRowKeys.size) {
            this.selectedRowKeys = nextProps.selectedRowKeys;
        }
    }

    renderCustomButtons = () => {
        const {
            props: { customButtonTexts, custonButtonIcons, onCustomButtonClicks }
        } = this;
        const bulkBtns = customButtonTexts.map((_, i) => (
            <Button
                key={i}
                icon={custonButtonIcons[i]}
                // size={"small"}
                style={{ boxShadow: "none", marginRight: "10px" }}
                onClick={() => onCustomButtonClicks[i]()}
            >
                {customButtonTexts[i]}
            </Button>
        ));
        return (
            <React.Fragment>
                {bulkBtns.map((_) => (
                    <>
                        {_}
                    </>
                ))}
            </React.Fragment>
        );
    }

    renderBulkActionButton = () => {
        const {
            props: { bulkAction, bulkActionName }
        } = this;
        const bulkBtns = bulkAction.map((_, i) => (
            <Button
                key={i}
                type={"primary"}
                icon={"check"}
                // size={"small"}
                style={{ boxShadow: "none", marginLeft: "10px" }}
                disabled={this.selectedRowKeys.size === 0}
                onClick={() => bulkAction[i](this.bulkActionSelectedRows)}
            >
                {bulkActionName[i]}
            </Button>
        ));

        return (
            <React.Fragment>
                {bulkBtns.map((_, i) => (
                    <Badge key={i} count={this.selectedRowKeys.size}>
                        {_}
                    </Badge>
                ))}
            </React.Fragment>
        );
    };
    isDataFiltered = false;
    onFilterApply = filters => {
        this.filters = Object.assign({}, filters);
        if (this.props.onFilter) {
            this.isDataFiltered = true;
            this.props.onFilter(filters);
            this.forceUpdate();
            return;
        }
        const { keyFilter } = this.props;
        this.filteredColumns = this.filteredColumns.map(_ => ({
            ..._,
            filteredValue: this.filters[_.key]
        }));
        if (this.isEntireTableSelected) {
            this.updateSelectedRows(
                this.filterRows()
                    .map(_ => _[keyFilter])
                    .toList()
            );
        } else {
            this.updateSelectedRows(
                this.filterRows(
                    this.props.data.filter(_ =>
                        this.selectedRowKeys.includes(_[keyFilter])
                    )
                )
                    .map(_ => _[keyFilter])
                    .toList()
            );
        }
        this.setState({
            selecttimestamp: new Date().toString()
        });
    };

    filterAccountStmt = () => {
        this.props.toggleAccountFilterModal();
    };

    //TABLE TOP SECTION CONFIG
    getTableTopSection = () => {
        if (!this.props.showTopSection) {
            return null;
        }
        const {
            title,
            showAssetsBtn,
            showBulkActionBtn,
            showXlsxBtn,
            spanConfig,
            showCustomButton,
            showFilters,
            showBulkDropdown,
            bulkActionDropdown,
            showApiXlsxDownload,
            showFilterBtn,
            showDownloadBtn,
            startDate,
            endDate,
            showDocumentFilter,
            hideColumnFiltersModal,
            bulkProceedCasesAction,
            customSelect,
        } = this.props;
        return (
            <Row className={cx("px-5")}>
                {title ? (
                    <Col span={spanConfig.title}>
                        {title}
                    </Col>
                ) : null}
                {showFilters ? (
                    <Col span={title ? spanConfig.filters : 12}>
                        <TableFilters
                            columns={this.filteredColumns}
                            filters={this.filters}
                            applyFilters={this.onFilterApply}
                            hideColumnFiltersModal={hideColumnFiltersModal}
                        />
                    </Col>
                ) : null}
                <Col offset={spanConfig.btnOffset} span={spanConfig.btnSpan}>
                    <div className={cx("d-flex justify-content-flex-end")}>
                        {showDocumentFilter ? (
                            <Select
                                onSelect={this.props.onSelectDocumentFilter}
                                value={this.props.containDocuments}
                                style={{
                                    marginRight: "10px",
                                    minWidth: "100px"
                                }}
                                dropdownMatchSelectWidth={false}
                                size={"small"}
                            >
                                {DOCUMENT_FILTER_OPTIONS.map((_, k) => (
                                    <Select.Option key={k} value={_}>
                                        {k}
                                    </Select.Option>
                                )).toList().toArray()}
                            </Select>
                        ) : null}
                        {showFilterBtn ? (
                            <Button
                                type={"default"}
                                onClick={this.filterAccountStmt}
                                icon={"download"}
                                size={"small"}
                                style={{
                                    marginLeft: "10px"
                                }}>
                                Filter Statement
                            </Button>
                        ) : null}
                        {showDownloadBtn ? (
                            <Button
                                type={"default"}
                                onClick={this.onDownload}
                                icon={"download"}
                                size={"small"}
                                style={{
                                    marginLeft: "10px"
                                }}>
                                Download
                            </Button>
                        ) : null}
                        {this.props.showTime ? (
                            <Tag
                                style={{
                                    fontWeight: 600,
                                    fontSize: "16px",
                                    justifyContent: "center"
                                }}>
                                {startDate
                                    ? startDate.format("DD-MM-YYYY")
                                    : moment()
                                        .subtract(1, "months")
                                        .format("DD-MM-YYYY")}{" "}
                                -{" "}
                                {endDate
                                    ? endDate.format("DD-MM-YYYY")
                                    : moment().format("DD-MM-YYYY")}
                            </Tag>
                        ) : null}
                        {showCustomButton ? (
                            this.renderCustomButtons()
                        ) : null}
                        {this.props.showColumnSelector ? 
                            (<TableColumnSelectorDropdown
                                columns={this.props.columns}
                                showColumnSelectorDrawer={this.state.showColumnSelectorDrawer}
                                toggleColumnSelector={() => this.setState({
                                    showColumnSelectorDrawer: !this.state.showColumnSelectorDrawer
                                })}
                                checkedColumns={this.checkedColumns}
                                onSelect={this.filterColumns}
                                columnLimit={this.props.columnLimit}
                            />) : 
                            null
                        }
                        {customSelect && <Select
                            value={this.props.customSelectValue}
                            onChange={this.props.customSelectOnChange}
                            style={this.props.customSelectStyle}
                            size={this.props.customSelectSize}
                            className={this.props.customSelectClassName}
                            placeholder={"Select a value"}
                            >
                            {this.props.customSelectOptions.map((option, index) => (
                                <Select.Option key={index} value={option.value ?? option}>
                                {option.label ?? option}
                                </Select.Option>
                            ))}
                            </Select>}
                        {this.props.customButtons?.map((btn, idx) => (
                            <Button
                                key={idx}
                                type="primary"
                                className={btn.className}
                                onClick={btn.onClick}
                                style={{ marginLeft: idx > 0 ? 8 : 0 }}
                            >
                                {btn.text}
                            </Button>
                        ))}
                        {showAssetsBtn ? (
                            <Button
                                type={"default"}
                                disabled={this.selectedRowKeys.size === 0}
                                icon={"download"}
                                style={{
                                    marginLeft: "10px"
                                }}
                                size={"small"}
                            >
                                Assets
                            </Button>
                        ) : null}
                        {showApiXlsxDownload ? (
                            <Button
                                type={"default"}
                                onClick={this.props.onApiXlsxDownload}
                                icon={"file-excel"}
                                size={"small"}
                                style={{
                                    marginLeft: "10px"
                                }}>
                                XLSX
                            </Button>
                        ) : null}
                        {bulkProceedCasesAction ? (
                            <Button
                                type={"default"}
                                disabled={this.selectedRowKeys.size === 0}
                                onClick={this.props.bulkProceedCasesModal}
                                // icon={"right"}
                                style={{
                                    marginLeft: "10px"
                                }}>
                                Proceed
                            </Button>
                        ) : null}
                        {showXlsxBtn ? (
                            <Button
                                type={"default"}
                                disabled={this.selectedRowKeys.size === 0}
                                onClick={this.onDownloadXlsx}
                                icon={"file-excel"}
                                // size={"small"}
                                style={{
                                    marginLeft: "10px"
                                }}>
                                XLSX
                            </Button>
                        ) : null}
                        {showBulkDropdown
                            ? bulkActionDropdown && (
                                <Badge count={this.selectedRowKeys.size}>
                                    {bulkActionDropdown({
                                        data: this.props.data,
                                        selectedKeys: this.selectedRowKeys,
                                        disabled:
                                              this.selectedRowKeys.size === 0,
                                        keyFilter: this.props.keyFilter
                                    })}
                                </Badge>
                            )
                            : null}
                        {showBulkActionBtn
                            ? this.renderBulkActionButton()
                            : null}
                    </div>
                </Col>
            </Row>
        );
    };

    handleSave = (values, e) => {
        
        if(e.target && e.target.className && e.target.className.includes("react-resizable-handle")) {
            return;
        }
        this.setState({
            selectedColumn: values.dataIndex,
            selectedDatatype: values.dataType
        });
    };

    handleResize = (dataIndex, e, { size }) => {
        e.stopPropagation();
        e.preventDefault();
        this.checkedColumns = this.checkedColumns.setIn([dataIndex, "width"], size.width);
        this.props.columns.map(_ => {
            const col = this.checkedColumns.get(_.key);
            if(col) {
                _.width = col.width;
                return _;
            }
            return _;
        });
        this.ownWidth = this.getTableWidth(this.checkedColumns);
        this.props.onColumnsUpdate(this.checkedColumns.map(_ => Map(_)));
        this.forceUpdate();
         
    };

    handleResizeEnd = () =>  {
        // this.forceUpdate();
    };

    getFooter = (data) => {
        if (this.props.showFooter) {
            return (
                <div className={"d-flex"}>
                    <div className={"fb-50"}>
                        <TableFooter
                            data={data}
                            dataType={this.state.selectedDatatype}
                            columnName={this.state.selectedColumn}
                            selectedRowKeys={this.selectedRowKeys}
                            keyFilter={this.props.keyFilter}
                        />
                    </div>
                    <div className={"fb-50 d-flex flex-row-reverse"}>
                        <Pagination {...this.props.pagination} size={"small"} />
                    </div>
                </div>
            );
        } else if(this.props.showPaginationOnly) {
            return(
                <div className={"d-flex"}>
                    <div className={"fb-50"}></div>
                    <div className={"fb-50 d-flex flex-row-reverse"}>
                        <Pagination {...this.props.pagination} size={"small"} />
                    </div>
                </div>
            );
        }
        return null;
    };
    render() {
        const {
            data,
            loading,
            scrollY,
            onRow,
            pagination,
            tablePagination,
            keyFilter,
            bordered,
            size,
            className,
            editable,
            type,
            components
        } = this.props;
        return (
            <ParentSize>
                {parent => {
                    this.updateParentWidth(parent);
                    this.table = (
                        <AntTable
                            {...(editable
                                ? {
                                    components: {
                                        body: {
                                            row: EditableRow,
                                            cell: EditableCell
                                        }
                                    }
                                }
                                : {})}
                            {...(components ? { components } : {})}
                            {...(this.props.showTopSection
                                ? { title: this.getTableTopSection }
                                : {})}
                            className={className}
                            expandIcon={this.props.expandIcon || null}
                            columns={this.checkedColumns
                                .toList()
                                .toArray()
                                /* eslint-disable no-unused-vars */
                                .map(
                                    ({
                                        filterIcon,
                                        filterDropdown,
                                        onHeaderCellGenerator,
                                        ...restColFields
                                    }) => {
                                        if (onHeaderCellGenerator) {
                                            return {
                                                onHeaderCell: onHeaderCellGenerator({
                                                    onClick: this.handleSave,
                                                    onResize: this.handleResize,
                                                    onResizeEnd: this.handleResizeEnd
                                                }),
                                                ...restColFields
                                            };
                                        }
                                        return { ...restColFields };
                                    }
                                )}
                            scroll={{
                                x: getScrollPercent(
                                    this.parentWidth,
                                    this.ownWidth
                                ),
                                y: (!pagination || scrollY > 0||scrollY == true) ? (scrollY == true ? (parent.height > 400 ? (parent.height - 120) : 400) : scrollY) : null
                            }}
                            dataSource={data.toArray()}
                            rowKey={_ => _[keyFilter]}
                            rowSelection={this.getRowSelectionConfig()}
                            bordered={this.props.bordered || false}
                            {...{
                                loading,
                                onRow,
                                size,
                                bordered,
                                pagination: pagination || (!tablePagination
                                    ? false
                                    : {
                                        size: "small",
                                        showSizeChanger: true,
                                        pageSizeOptions: [
                                            "10",
                                            "20",
                                            "50",
                                            "100",
                                            "500"
                                        ],
                                        position: "bottom",
                                        count: data.size
                                    }
                                )}}
                            rowClassName={(record, index) => "editable-row " + this.props.rowClassName(record, index)}
                            {...(this.props.showFooter || this.props.showPaginationOnly
                                ? { footer: this.getFooter }
                                : {})}
                            {...(this.props.emptyText
                                ? {
                                    locale: {
                                        emptyText: this.props.emptyText
                                    }
                                }
                                : {})}
                            size={this.props.size}
                            onExpand={this.props.onExpand}
                            expandIconColumnIndex={this.props.expandIconColumnIndex}
                            // expandedRowKeys={this.props.expandedRowKeys}
                            expandIconAsCell={this.props.expandIconAsCell}
                            expandedRowRender={this.props.expandedRowRender}
                            // tableLayout={"auto"}
                        />
                    );
                    return type === "inner" ? (
                        this.table
                    ) : (
                        <Card className="padless-card">{this.table}</Card>
                    );
                }}
            </ParentSize>
        );
    }
}

Table.propTypes = {
    title: PropTypes.node,
    editable: PropTypes.bool,
    columns: PropTypes.object,
    onExpand: PropTypes.object,
    checkedColumns: PropTypes.array.isRequired,
    data: PropTypes.object.isRequired,
    keyFilter: PropTypes.string,
    bulkAction: PropTypes.array,
    bulkActionDropdown: PropTypes.oneOfType([PropTypes.node, PropTypes.object]),
    bulkActionName: PropTypes.array,
    loading: PropTypes.bool,
    csvSuffix: PropTypes.string,
    showAssetsBtn: PropTypes.bool,
    showXlsxBtn: PropTypes.bool,
    showCustomButton: PropTypes.bool,
    showBulkActionBtn: PropTypes.bool,
    showBulkDropdown: PropTypes.bool,
    showTopSection: PropTypes.bool,
    showDateRangePicker: PropTypes.bool,
    showFilters: PropTypes.bool,
    showFooter: PropTypes.bool,
    scrollY: PropTypes.oneOfType([PropTypes.boolean, PropTypes.string]),
    scrollX: PropTypes.bool,
    showHeader: PropTypes.bool,
    onRow: PropTypes.func,
    pagination: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
    noRowSelection: PropTypes.bool,
    onFilter: PropTypes.func,
    size: PropTypes.string,
    bordered: PropTypes.bool,
    fixedColsLeft: PropTypes.array,
    fixedColsRight: PropTypes.array,
    spanConfig: PropTypes.object,
    className: PropTypes.string,
    emptyText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    type: PropTypes.string,
    showColumnSelector: PropTypes.bool,
    ignoreKeyFilter: PropTypes.bool,
    showPaginationOnly: PropTypes.bool,
    showTotalPages: PropTypes.bool,
    totalPagesView: PropTypes.object,
    showApiXlsxDownload: PropTypes.bool,
    onApiXlsxDownload: PropTypes.func,
    onCustomButtonClicks: PropTypes.array,
    customButtonTexts: PropTypes.array,
    custonButtonIcons: PropTypes.array,
    toggleAccountFilterModal: PropTypes.func,
    showTime: PropTypes.bool,
    showDownloadBtn: PropTypes.bool,
    showFilterBtn: PropTypes.bool,
    tablePagination: PropTypes.bool,
    downloadAccountStmt: PropTypes.func,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    onRowSelection: PropTypes.func,
    expandedRowRender: PropTypes.func,
    bulkActionSelectedKey: PropTypes.string,
    showDocumentFilter: PropTypes.bool,
    onSelectDocumentFilter: PropTypes.func,
    containDocuments: PropTypes.string,
    expandIcon: PropTypes.func,
    expandIconColumnIndex: PropTypes.number,
    expandedRowKeys: PropTypes.array,
    expandIconAsCell: PropTypes.bool,
    components: PropTypes.object,
    columnLimit: PropTypes.number,
    onColumnsUpdate: PropTypes.func,
    filterColumns: PropTypes.array,
    hideColumnFiltersModal : PropTypes.bool,
    selectedRowKeys : PropTypes.array,
    rowClassName: PropTypes.func
};

Table.defaultProps = {
    editable: false,
    showXlsxBtn: false,
    showColumnSelector: true,
    showBulkActionBtn: false,
    showAssetsBtn: false,
    showTopSection: true,
    showDateRangePicker: false,
    showFilters: true,
    showFooter: false,
    checkedColumns: [],
    noRowSelection: false,
    csvSuffix: "invoices",
    pagination: {
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "30"],
        position: "bottom"
    },
    size: "middle",
    bordered: false,
    onRow: null,
    className: cx("padless-table", "custom-table"),
    spanConfig: {
        title: 4,
        filters: 12,
        btnSpan: 8,
        btnOffset: 0
    },
    fixedColsLeft: [],
    fixedColsRight: [],
    emptyText: null,
    type: "outer",
    ignoreKeyFilter: false,
    showPaginationOnly: false,
    showTotalPages: false,
    showApiXlsxDownload: false,
    showTime: false,
    showDownloadBtn: false,
    showFilterBtn: false,
    tablePagination: false,
    onRowSelection: _noop,
    expandedRowRender: null,
    onExpand: null,
    expandIconColumnIndex: 0,
    expandedRowKeys: [],
    bulkActionSelectedKey: "case_task_id",
    showDocumentFilter: false,
    filterColumns: [],
    hideColumnFiltersModal : false,
    rowClassName : _noop
};

export default Table;
